'use client';

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { useEcho } from '@/contexts/echo/echo-provider';
import { toast } from '@/components/core/v2/toaster';
import { getNotificationLink } from './notification-link';

export interface AppNotification {
  id: string;
  type: string;
  data: {
    type?: string;
    title?: string;
    body?: string;
    request_id?: string | number;
    request_uuid?: string;
    requestId?: string | number;
    reservation_id?: string | number;
    reservation_uuid?: string;
    reservationId?: string | number;
    announcement_id?: string | number;
    announcement_uuid?: string;
    community_id?: string | number;
    [key: string]: unknown;
  };
  read_at: string | null;
  created_at: string;
}

export interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user } = useUser();
  const { echo } = useEcho();
  const navigate = useNavigate();

  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  // Keep navigate in a ref so the Echo listener closure always uses the latest.
  const navigateRef = React.useRef(navigate);
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Ref to markAsRead so the Echo listener / toast action can call the latest.
  const markAsReadRef = React.useRef<(id: string) => Promise<void>>(async () => {});

  // Keep the active community in a ref so the Echo listener can filter live
  // notifications without resubscribing every time the user switches community.
  const activeCommunityRef = React.useRef<number | string | null | undefined>(user?.selected_community_id);
  React.useEffect(() => {
    activeCommunityRef.current = user?.selected_community_id;
  }, [user?.selected_community_id]);

  const fetchNotifications = React.useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const res = await axios.get('/api/v2/notifications');
      setNotifications(res.data.notifications ?? []);
      setUnreadCount(res.data.unread_count ?? 0);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initial fetch when user is available
  React.useEffect(() => {
    if (user) {
      fetchNotifications().catch(() => {});
    }
  }, [user, fetchNotifications]);

  // Subscribe to private Reverb channel for real-time notifications
  // Laravel broadcasts to "App.Models.User.{id}" (Echo adds the "private-" prefix)
  React.useEffect(() => {
    if (!echo || !user?.id) return;

    const channelName = `App.Models.User.${user.id}`;
    console.log('[NotificationProvider] Subscribing to private channel:', channelName);

    const channel = echo.private(channelName);

    // Laravel's BroadcastNotificationCreated fires this event
    channel.listen('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', (data: any) => {
      console.log('[NotificationProvider] Notification received:', data);
      const newNotif: AppNotification = {
        id: data.id ?? String(Date.now()),
        type: data.type ?? 'App\\Notifications\\Unknown',
        data: {
          type: data.type_data ?? data.notification_type,
          title: data.title,
          body: data.body,
          request_id: data.request_id,
          community_id: data.community_id,
          ...data,
        },
        read_at: null,
        created_at: new Date().toISOString(),
      };

      // Community-scoped notifications only belong to the community the user is
      // currently viewing. Ignore live notifications for other communities so
      // they don't surface here or navigate to objects outside this community.
      // Notifications without a community_id (account-level) always pass through.
      const activeCommunity = activeCommunityRef.current;
      const notifCommunity = newNotif.data.community_id;
      if (
        notifCommunity != null &&
        activeCommunity != null &&
        String(notifCommunity) !== String(activeCommunity)
      ) {
        return;
      }

      setNotifications((prev) => [newNotif, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Surface a toast for the live notification, clickable to the related object.
      const link = getNotificationLink(newNotif);
      const toastTitle = newNotif.data.title || 'New notification';
      toast(toastTitle, {
        description: newNotif.data.body as string | undefined,
        ...(link
          ? {
              action: {
                label: 'View',
                onClick: () => {
                  if (newNotif.id) {
                    markAsReadRef.current(newNotif.id).catch(() => {});
                  }
                  navigateRef.current(link);
                },
              },
            }
          : {}),
      });
    });

    return () => {
      console.log('[NotificationProvider] Leaving channel:', channelName);
      echo.leave(channelName);
    };
  }, [echo, user?.id]);

  const markAsRead = React.useCallback(async (id: string) => {
    await axios.put(`/api/v2/notifications/${id}/read`);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  React.useEffect(() => {
    markAsReadRef.current = markAsRead;
  }, [markAsRead]);

  const markAllAsRead = React.useCallback(async () => {
    await axios.put('/api/v2/notifications/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
    setUnreadCount(0);
  }, []);

  const dismiss = React.useCallback(async (id: string) => {
    await axios.delete(`/api/v2/notifications/${id}`);
    setNotifications((prev) => {
      const removed = prev.find((n) => n.id === id);
      if (removed && !removed.read_at) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  const value = React.useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      dismiss,
      refresh: fetchNotifications,
    }),
    [notifications, unreadCount, isLoading, markAsRead, markAllAsRead, dismiss, fetchNotifications]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
